import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../App.jsx';
import { Icon, Money, PersonDot, Seg, StatusBadge } from './ui.jsx';
import { monthLabel, addMonths, yearOf } from '../lib/calendar.js';
import { fmt, fmtPlain, parseMoney, sum } from '../lib/money.js';
import { updateReview } from '../lib/actions.js';

// Cells that can be typed into, per person: take-home received and spending beyond budget
const EDIT_COLS = ['net', 'over'];

function EditCell({ entry, col, focused, tabbable, onFocus, onMove, onSave, onLocked }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const ref = useRef(null);
  const items = entry.review.extraSpending || [];
  const fromSheet = col === 'net' ? entry.fromSheet.actualNet : entry.fromSheet.extraSpending;
  const locked = entry.confirmed || fromSheet || (col === 'over' && items.length > 1);
  const actualValue = col === 'net' ? (typeof entry.review.actualNet === 'number' ? entry.review.actualNet : null) : (items.length ? entry.actual.extraSpending : null);
  const shown = col === 'net' ? entry.actual.net : entry.actual.extraSpending;
  const expected = col === 'net' ? entry.expected.net : null;

  useEffect(() => { if (focused && !editing) ref.current?.focus(); }, [focused, editing]);

  const start = (initial) => {
    if (locked) { onLocked(entry, fromSheet ? 'sheet' : col === 'over' && items.length > 1 ? 'items' : 'confirmed'); return; }
    setText(initial !== undefined ? initial : actualValue === null ? '' : fmtPlain(actualValue));
    setEditing(true);
  };
  const parsed = parseMoney(text, { allowEmpty: true, allowNegative: col === 'over' });
  const commit = (move) => {
    if (!parsed.ok) return;
    setEditing(false);
    if ((parsed.value ?? null) !== (actualValue ?? null)) onSave(entry, col, parsed.value);
    if (move) onMove(move);
    else setTimeout(() => ref.current?.focus(), 0);
  };

  return (
    <td
      ref={ref}
      role="gridcell"
      className={`cell-edit ${focused ? 'focused' : ''}`}
      tabIndex={focused || tabbable ? 0 : -1}
      aria-label={`${entry.name}, ${monthLabel(entry.month)}, ${col === 'net' ? 'take-home received' : 'spent beyond budget'}: ${fmt(shown)}${locked ? ' (locked)' : ''}`}
      onClick={() => { onFocus(); if (!editing) start(); }}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (editing) return;
        if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); start(); }
        else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); if (locked) onLocked(entry, 'confirmed'); else if (actualValue !== null) onSave(entry, col, null); }
        else if (/^[0-9,.\-=]$/.test(e.key)) { e.preventDefault(); start(e.key); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); onMove('down'); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); onMove('up'); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); onMove('left'); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); onMove('right'); }
      }}
    >
      {editing ? (
        <input
          className="cell-input"
          autoFocus
          inputMode="decimal"
          value={text}
          aria-label="Amount"
          aria-invalid={!parsed.ok}
          style={!parsed.ok ? { boxShadow: 'inset 0 0 0 1px var(--danger)' } : undefined}
          title={!parsed.ok ? parsed.error : 'Enter to save, Esc to cancel, empty = as expected'}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => { if (parsed.ok) commit(null); else setEditing(false); }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); commit('down'); }
            else if (e.key === 'Tab') { e.preventDefault(); commit(e.shiftKey ? 'left' : 'right'); }
            else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setTimeout(() => ref.current?.focus(), 0); }
          }}
        />
      ) : (
        <>
          <span className={actualValue === null && !fromSheet ? 'muted' : ''}>{col === 'over' && !shown ? <span className="muted">—</span> : <Money value={shown} />}</span>
          {col === 'net' && actualValue !== null && Math.abs(actualValue - expected) >= 0.01 && <span className="exp">exp. {fmt(expected)}</span>}
          {fromSheet && <span className="exp">from sheet</span>}
        </>
      )}
    </td>
  );
}

export default function Months() {
  const { data, model, commit, openReview, openMonth, toast } = useApp();
  const [range, setRange] = useState('near');
  const people = data.people;
  const rows = useMemo(() => {
    let ms = model.months;
    if (range === 'near') ms = ms.filter((m) => m.key <= addMonths(model.current, 6));
    if (range === 'pending') ms = ms.filter((m) => m.status === 'pending' || m.status === 'partial');
    return ms;
  }, [model, range]);
  const [focus, setFocus] = useState(null); // { r, c }
  const editCols = people.flatMap((p) => EDIT_COLS.map((c) => ({ pid: p.id, col: c })));

  const move = (dir) => {
    setFocus((f) => {
      if (!f) return f;
      const findRow = (r, step) => {
        for (let i = r + step; i >= 0 && i < rows.length; i += step) if (rows[i].people.some((e) => e.personId === editCols[f.c].pid)) return i;
        return r;
      };
      if (dir === 'down') return { ...f, r: findRow(f.r, 1) };
      if (dir === 'up') return { ...f, r: findRow(f.r, -1) };
      if (dir === 'left') return { ...f, c: Math.max(0, f.c - 1) };
      if (dir === 'right') return { ...f, c: Math.min(editCols.length - 1, f.c + 1) };
      return f;
    });
  };

  const save = (entry, col, value) => {
    const label = `${entry.name} · ${monthLabel(entry.month)}: ${col === 'net' ? 'take-home' : 'beyond budget'} ${value === null ? 'cleared' : fmt(value)}`;
    commit(label, (s) => updateReview(s, entry.month, entry.personId, col === 'net'
      ? { actualNet: value }
      : { extraSpending: value === null || value === 0 ? null : [{ id: entry.review.extraSpending?.[0]?.id, label: entry.review.extraSpending?.[0]?.label || '', amount: value }] }));
  };
  const onLocked = (entry, why) => {
    if (why === 'sheet') toast(`That comes from ${entry.name}'s spreadsheet; change it there.`);
    else if (why === 'items') { toast('Several lines were entered: opening the payday.'); openReview(entry.month, entry.personId); }
    else { toast('This payday is confirmed. Opening it so you can reopen it.'); openReview(entry.month, entry.personId); }
  };

  let lastYear = null;
  const yearTotal = (y) => sum(model.months.filter((m) => m.year === y && m.key <= model.current).map((m) => m.savings));

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Months</h1>
          <p>Each payday adds its expected savings; type what really arrived. Click a month for the details.</p>
        </div>
        <span className="spacer" />
        <Seg ariaLabel="Which months" value={range} onChange={setRange} options={[{ value: 'near', label: 'Up to 6 months ahead' }, { value: 'all', label: 'All' }, { value: 'pending', label: `To review (${model.pending.length})` }]} />
      </div>
      <p className="small muted hide-sm">Spreadsheet keys: click a cell or use the arrows · type or press Enter to edit · Enter saves and goes down, Tab saves and goes right · Esc cancels · Delete goes back to the expected amount · sums like 1500-23,5 work.</p>
      {range === 'pending' && !rows.length ? <div className="card empty"><Icon name="check" size={20} /><p>Everything is reviewed.</p></div> : (
        <div className="table-wrap" style={{ maxHeight: '72vh' }}>
          <table className="sheet" role="grid" aria-label="Months">
            <thead>
              <tr>
                <th rowSpan={2} className="left">Month</th>
                {people.map((p) => <th key={p.id} colSpan={4} className="group sep"><span className="pill-person" style={{ justifyContent: 'center' }}><PersonDot color={p.color} />{p.name}</span></th>)}
                <th colSpan={4} className="group sep">Together</th>
              </tr>
              <tr>
                {people.map((p) => [
                  <th key={`${p.id}s`} className="sep left">Status</th>,
                  <th key={`${p.id}n`}>Take-home</th>,
                  <th key={`${p.id}o`}>Beyond budget</th>,
                  <th key={`${p.id}c`}>To savings</th>,
                ])}
                <th className="sep">Joint expenses</th>
                <th>Over budget</th>
                <th>Saved</th>
                <th className="total">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m, r) => {
                const out = [];
                if (m.year !== lastYear) {
                  lastYear = m.year;
                  out.push(
                    <tr key={`y${m.year}`} className="year">
                      <td className="left">{m.year}</td>
                      <td colSpan={people.length * 4 + 2} className="left small">{yearOf(model.current) >= m.year ? `saved so far: ${fmt(yearTotal(m.year))}` : 'projected'}</td>
                      <td colSpan={2} />
                    </tr>,
                  );
                }
                out.push(
                  <tr key={m.key} className={`${m.isFuture ? 'future' : ''} ${m.isCurrent ? 'current' : ''}`}>
                    <td className="left"><button type="button" className="linkbtn" onClick={() => openMonth(m.key)} style={{ color: 'inherit' }}>{monthLabel(m.key, { short: true })}</button></td>
                    {people.map((p) => {
                      const e = m.people.find((x) => x.personId === p.id);
                      if (!e) return [<td key={`${p.id}s`} className="sep" />, <td key={`${p.id}n`} />, <td key={`${p.id}o`} />, <td key={`${p.id}c`} />];
                      return [
                        <td key={`${p.id}s`} className="sep left">
                          {e.status === 'pending' ? <button type="button" className="badge pending" style={{ cursor: 'pointer' }} aria-label={`Review ${p.name} ${monthLabel(m.key)}`} onClick={() => openReview(m.key, p.id)}>Review</button>
                            : <button type="button" style={{ background: 'none', border: 0, padding: 0 }} onClick={() => openReview(m.key, p.id)} aria-label={`Open ${p.name} ${monthLabel(m.key)}`}><StatusBadge status={e.status} source={e.source} /></button>}
                        </td>,
                        ...EDIT_COLS.map((col) => {
                          const c = editCols.findIndex((x) => x.pid === p.id && x.col === col);
                          return <EditCell key={`${p.id}${col}`} entry={e} col={col} focused={focus?.r === r && focus?.c === c} tabbable={!focus && r === 0 && c === 0} onFocus={() => setFocus({ r, c })} onMove={move} onSave={save} onLocked={onLocked} />;
                        }),
                        <td key={`${p.id}c`}><Money value={e.actual.contribution} />{Math.abs(e.difference) >= 0.01 && <span className="exp">exp. {fmt(e.expected.contribution)}</span>}</td>,
                      ];
                    })}
                    <td className="sep"><Money value={m.jointBudget} blankZero /></td>
                    <td>{m.variance ? <Money value={m.variance} /> : <span className="muted">—</span>}</td>
                    <td><Money value={m.savings} color={!m.isFuture} /></td>
                    <td className="total"><Money value={m.balance} /></td>
                  </tr>,
                );
                return out;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
